import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsHeatmap from "highcharts/modules/heatmap";
import { useRef } from "react";
import { t } from "i18next";

// Initialize heatmap module
if (typeof Highcharts === 'object') {
    HighchartsHeatmap(Highcharts);
}

interface HeatmapDataPoint {
    x: number;
    y: number;
    value: number;
    name?: string;
}

interface LivisHeatmapHighchartsProps {
    data: HeatmapDataPoint[];
    height?: number;
    type?: 'heatmap' | 'bubble' | 'column' | 'scatter';
    tooltip?: boolean;
    colorAxis?: {
        min?: number;
        max?: number;
        minColor?: string;
        maxColor?: string;
        stops?: Array<[number, string]>;
    };
    xAxisCategories?: string[];
    yAxisCategories?: string[];
    title?: string;
    subtitle?: string;
    dataLabels?: boolean;
    borderWidth?: number;
    borderColor?: string;
    noDataMessage?: string; // NEW: Custom no data message
}

const LivisHeatmapHighcharts = ({
    data,
    height = 400,
    type = 'heatmap',
    tooltip = true,
    colorAxis = {
        min: 0,
        minColor: '#E8F5E8',
        maxColor: '#4CAF50'
    },
    xAxisCategories = ['Shift 01', 'Shift 02', 'Shift 03'],
    yAxisCategories = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    title,
    subtitle,
    dataLabels = true,
    borderWidth = 1,
    borderColor = '#FFFFFF',
    noDataMessage = "No Record Found" // NEW: Default message
}: LivisHeatmapHighchartsProps) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    console.log('LivisHeatmapHighcharts type:', type);

    // NEW: Check for empty data and render no data message
    if (!data || data.length === 0) {
        return (
            <div 
                className="livis-dashboard-no-record-centered"
                style={{ 
                    width: '100%', 
                    height: height, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                }}
            >
                <p>{t(noDataMessage)}</p>
            </div>
        );
    }

    // Base configuration
    const baseOptions: Highcharts.Options = {
        chart: {
            type: type,
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'Inter, sans-serif'
            },
            // spacing: [20, 20, 20, 20],
            height: height
        },
        title: title ? {
            text: title,
            style: {
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--livis-gray-font-color)'
            }
        } : undefined,
        subtitle: subtitle ? {
            text: subtitle,
            style: {
                fontSize: '14px',
                color: '#64748B'
            }
        } : undefined,
        xAxis: {
            categories: xAxisCategories,
            title: {
                text: null
            },
            labels: {
                style: {
                    color: 'var(--livis-gray-font-color)',
                    fontSize: '14px',
                    fontWeight: '500'
                }
            },
            lineWidth: 0,
            tickWidth: 0
        },
        yAxis: {
            categories: yAxisCategories,
            title: {
                text: null
            },
            labels: {
                style: {
                    color: 'var(--livis-gray-font-color)',
                    fontSize: '14px',
                    fontWeight: '500'
                }
            },
            lineWidth: 0,
            tickWidth: 0,
            reversed: true
        },
        tooltip: {
            enabled: tooltip,
            backgroundColor: 'var(--livis-recipe-details-part-footer-color)',
            borderWidth: 0,
            borderRadius: 8,
            shadow: false,
            style: {
                color: 'var(--livis-white-color)',
                fontSize: '14px'
            }
        },
        legend: {
            enabled: false
        },
        credits: {
            enabled: false
        }
    };

    // Type-specific configurations
    let typeSpecificOptions: Partial<Highcharts.Options> = {};

    switch (type) {
        case 'heatmap':
            typeSpecificOptions = {
                colorAxis: {
                    min: colorAxis.min,
                    max: colorAxis.max,
                    minColor: colorAxis.minColor,
                    maxColor: colorAxis.maxColor,
                    stops: colorAxis.stops,
                    labels: {
                        style: {
                            color: '#64748B',
                            fontSize: '12px'
                        }
                    }
                },
                plotOptions: {
                    heatmap: {
                        borderWidth: borderWidth,
                        borderColor: borderColor,
                        dataLabels: {
                            enabled: dataLabels,
                            color: '#1E293B',
                            style: {
                                fontSize: '14px',
                                fontWeight: '600',
                                textOutline: 'none'
                            },
                            formatter: function () {
                                return this.point.value?.toLocaleString();
                            }
                        }
                    }
                },
                tooltip: {
                    ...baseOptions.tooltip,
                    formatter: function () {
                        return `<b>${yAxisCategories[this.point.y!]} - ${xAxisCategories[this.point.x!]}</b><br/>
                                Count: <b>${this.point.value?.toLocaleString()}</b>`;
                    }
                },
                series: [{
                    type: 'heatmap',
                    name: 'Inspection Count',
                    data: data
                }]
            };
            break;

        case 'bubble':
            typeSpecificOptions = {
                plotOptions: {
                    bubble: {
                        dataLabels: {
                            enabled: dataLabels,
                            color: '#1E293B',
                            style: {
                                fontSize: '12px',
                                fontWeight: '600',
                                textOutline: 'none'
                            },
                            formatter: function () {
                                return (this.point as any).z?.toLocaleString();
                            }
                        },
                        color: '#4CAF50',
                        marker: {
                            fillOpacity: 0.7,
                            lineWidth: 1,
                            lineColor: '#FFFFFF'
                        }
                    }
                },
                tooltip: {
                    ...baseOptions.tooltip,
                    formatter: function () {
                        return `<b>${yAxisCategories[this.point.y!]} - ${xAxisCategories[this.point.x!]}</b><br/>
                                Count: <b>${(this.point as any).z?.toLocaleString()}</b>`;
                    }
                },
                series: [{
                    type: 'bubble',
                    name: 'Inspection Count',
                    data: data.map(point => ({
                        x: point.x,
                        y: point.y,
                        z: point.value
                    }))
                }]
            };
            break;

        case 'column': {
            // Transform data for column chart - wrapped in braces to fix ESLint error
            const columnData = data.map(point => ({
                name: `${yAxisCategories[point.y]} - ${xAxisCategories[point.x]}`,
                y: point.value,
                color: '#4CAF50'
            }));

            typeSpecificOptions = {
                xAxis: {
                    type: 'category',
                    labels: {
                        rotation: -45,
                        style: {
                            color: '#1E293B',
                            fontSize: '12px'
                        }
                    }
                },
                yAxis: {
                    title: {
                        text: 'Count',
                        style: {
                            color: '#1E293B',
                            fontSize: '14px'
                        }
                    },
                    labels: {
                        style: {
                            color: '#1E293B',
                            fontSize: '12px'
                        }
                    }
                },
                plotOptions: {
                    column: {
                        borderRadius: 4,
                        dataLabels: {
                            enabled: dataLabels,
                            style: {
                                color: '#1E293B',
                                fontSize: '12px',
                                fontWeight: '600'
                            },
                            formatter: function () {
                                return this.y?.toLocaleString();
                            }
                        }
                    }
                },
                tooltip: {
                    ...baseOptions.tooltip,
                    formatter: function () {
                        return `<b>${this.point.name}</b><br/>Count: <b>${this.y?.toLocaleString()}</b>`;
                    }
                },
                series: [{
                    type: 'column',
                    name: 'Inspection Count',
                    data: columnData
                }]
            };
            break;
        }

        case 'scatter':
            typeSpecificOptions = {
                plotOptions: {
                    scatter: {
                        marker: {
                            radius: 8,
                            fillColor: '#4CAF50',
                            lineWidth: 2,
                            lineColor: '#FFFFFF'
                        },
                        dataLabels: {
                            enabled: dataLabels,
                            color: '#1E293B',
                            style: {
                                fontSize: '12px',
                                fontWeight: '600',
                                textOutline: 'none'
                            },
                            formatter: function () {
                                return this.point.value?.toLocaleString();
                            }
                        }
                    }
                },
                tooltip: {
                    ...baseOptions.tooltip,
                    formatter: function () {
                        return `<b>${yAxisCategories[this.point.y!]} - ${xAxisCategories[this.point.x!]}</b><br/>
                                Count: <b>${this.point.value?.toLocaleString()}</b>`;
                    }
                },
                series: [{
                    type: 'scatter',
                    name: 'Inspection Count',
                    data: data.map(point => ({
                        x: point.x,
                        y: point.y,
                        value: point.value
                    }))
                }]
            };
            break;
    }

    // Merge configurations
    const options: Highcharts.Options = {
        ...baseOptions,
        ...typeSpecificOptions
    };

    return (
        <HighchartsReact
            highcharts={Highcharts}
            options={options}
            ref={chartRef}
        />
    );
};

export default LivisHeatmapHighcharts;